import { useEffect, useState } from 'react';
import { hasRole } from '../config/roles';

const API_ROLES = 'https://dev.sliitleo.org/Backend/roles.php';
const API_PATHS = 'https://dev.sliitleo.org/Backend/paths.php';
const API_ROLE_PATHS = 'https://dev.sliitleo.org/Backend/role_paths.php';

// roles.php/paths.php return role-independent lists reused by every
// consumer and every role change -- fetch them once per page load instead
// of once per consumer. Cleared on failure so the next call can retry.
let rolesPathsPromise = null;
function loadRolesAndPaths() {
  if (!rolesPathsPromise) {
    rolesPathsPromise = Promise.all([fetch(API_ROLES), fetch(API_PATHS)])
      .then(async ([rolesRes, pathsRes]) => {
        const rolesBody = await rolesRes.json();
        const pathsBody = await pathsRes.json();
        if (!rolesBody?.success || !pathsBody?.success) throw new Error('bad response');
        return { roles: rolesBody.data || [], paths: pathsBody.data || [] };
      })
      .catch((e) => {
        rolesPathsPromise = null;
        throw e;
      });
  }
  return rolesPathsPromise;
}

// role_paths.php is role-specific, unlike roles.php/paths.php above -- but
// still safe to cache per roleId for the whole page session. Without this,
// scaling dynamicPathKey from 1 route (Installation) to ~34 meant every
// single navigation re-fetched the same role's saved permissions from
// scratch, adding a network round-trip + loading gate to every page in the
// app. Cleared per-roleId on failure so a retry isn't stuck on a bad promise.
const rolePathsCache = new Map();
function loadRolePaths(roleId) {
  if (!rolePathsCache.has(roleId)) {
    const promise = fetch(`${API_ROLE_PATHS}?roleId=${roleId}`)
      .then((res) => res.json())
      .catch((e) => {
        rolePathsCache.delete(roleId);
        throw e;
      });
    rolePathsCache.set(roleId, promise);
  }
  return rolePathsCache.get(roleId);
}

// PathManagement.jsx calls this right after a successful "Save access" so
// an admin testing changes against their own role sees the effect on their
// very next navigation, instead of a stale in-session cache until reload.
export function invalidateRolePermissionsCache(roleId) {
  if (roleId != null) rolePathsCache.delete(roleId);
  else rolePathsCache.clear();
}

const EMPTY_STATE = {
  loading: true,
  hasSavedRouteRows: false,
  savedRoutes: new Set(),     // route strings, e.g. '/installation' -- Sidebar matches nav items by route
  savedRouteKeys: new Set(),  // path keys, e.g. 'installation' -- ProtectedRoute matches by key (a route's
                               // sub-paths, like /installation/scan, share one key, not one exact pathname)
  hasSavedPermissionRows: false,
  savedPermissionKeys: new Set(),
};

/**
 * Role & Path Management (roles/paths/role_paths tables) lets an admin save
 * arbitrary role_paths rows for a role -- some are real nav routes
 * (paths.type === 'route', the only kind that existed before this hook),
 * some are non-navigable permission markers like "installation_verify"
 * (paths.type === 'permission', added alongside this hook -- see
 * migrate_add_path_type_and_installation_permissions.php).
 *
 * Every consumer (ProtectedRoute, Sidebar, Installation.jsx) must treat
 * "has this role saved anything for kind K" as two INDEPENDENT questions,
 * one per type -- not "has this role saved anything at all". A role with
 * 12 saved route rows but zero permission rows must still fall back to its
 * hardcoded permission default; conflating the two is exactly the bug a
 * prior attempt hit (see the removed useRolePrivilege('home_birthdays')
 * experiment documented in a comment in src/pages/Dashboard.jsx) -- it
 * silently revoked an unrelated privilege the moment ANY page-access row
 * existed for System Admin/President.
 */
export default function useRolePermissions(userRole, { enabled = true } = {}) {
  const [state, setState] = useState(EMPTY_STATE);

  useEffect(() => {
    // Callers that don't need this (every route/component that never
    // passes dynamicPathKey/checks a permission) skip the fetch entirely --
    // the hook can still always be called unconditionally (Rules of Hooks)
    // without adding a network request to pages that don't use it.
    if (!enabled) {
      setState({ ...EMPTY_STATE, loading: false });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    (async () => {
      try {
        const { roles, paths } = await loadRolesAndPaths();
        const matchedRole = roles.find(
          (r) => String(r.name || '').trim().toLowerCase() === String(userRole || '').trim().toLowerCase()
        );
        if (!matchedRole) {
          if (!cancelled) setState({ ...EMPTY_STATE, loading: false });
          return;
        }

        const rpBody = await loadRolePaths(matchedRole.id);
        const savedKeys = (rpBody?.success && Array.isArray(rpBody.data)) ? rpBody.data.map((e) => e.key) : [];

        const keyToPath = new Map(paths.map((p) => [p.key, p]));
        const savedRoutes = new Set();
        const savedRouteKeys = new Set();
        const savedPermissionKeys = new Set();
        savedKeys.forEach((key) => {
          const p = keyToPath.get(key);
          if (!p) return; // stale key (path since renamed/removed) -- ignore
          if (p.type === 'permission') {
            savedPermissionKeys.add(key);
          } else {
            savedRoutes.add(p.route);
            savedRouteKeys.add(key);
          }
        });

        if (!cancelled) {
          setState({
            loading: false,
            hasSavedRouteRows: savedRouteKeys.size > 0,
            savedRoutes,
            savedRouteKeys,
            hasSavedPermissionRows: savedPermissionKeys.size > 0,
            savedPermissionKeys,
          });
        }
      } catch {
        // Network/parse failure -- behave exactly as "nothing configured",
        // never as "everything denied". Every fallbackRoles caller below
        // then decides access exactly as it did before this hook existed.
        if (!cancelled) setState({ ...EMPTY_STATE, loading: false });
      }
    })();

    return () => { cancelled = true; };
  }, [userRole, enabled]);

  return state;
}

// System Admin administers Path Management itself -- if it can be
// restricted by Path Management data, a single wrong/missing checkbox (or
// stale legacy data, as happened once already) locks out the one role that
// could otherwise fix it, with no recovery path short of a direct database
// edit. So it's structurally exempt here, unconditionally, regardless of
// what's ever saved in role_paths for it: always full access to every
// route and every permission. This is intentionally NOT configurable from
// the Path Management UI -- it's a hard safety floor, not a default that
// can be saved over.
const NEVER_RESTRICTED_ROLES = ['System Admin'];

/**
 * Route-level check for ProtectedRoute, matched by path KEY (not the raw
 * browser pathname) -- so a sub-route like /installation/scan shares the
 * same 'installation' key's permission as /installation itself, instead of
 * needing its own separate paths row and separately-saved access. No saved
 * route rows for this role -> fallbackRoles decides (today's exact
 * hardcoded behavior, so no existing user is ever locked out by this
 * shipping). Saved route rows exist -> pathKey must be explicitly among
 * them.
 */
export function canAccessRoute(pathKey, perm, userRole, fallbackRoles) {
  if (hasRole(userRole, NEVER_RESTRICTED_ROLES)) return true;
  if (!perm.hasSavedRouteRows) return hasRole(userRole, fallbackRoles);
  return perm.savedRouteKeys.has(pathKey);
}

/**
 * Permission-level check for UI elements (Verify/Check-in buttons, etc).
 * No saved permission rows for this role -> fallbackRoles decides (today's
 * exact hardcoded behavior). Saved permission rows exist -> key must be
 * explicitly among them.
 */
export function hasPermission(key, perm, userRole, fallbackRoles) {
  if (hasRole(userRole, NEVER_RESTRICTED_ROLES)) return true;
  if (!perm.hasSavedPermissionRows) return hasRole(userRole, fallbackRoles);
  return perm.savedPermissionKeys.has(key);
}
